import {
  Alert,
  Anchor,
  Button,
  Card,
  Center,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useState } from 'react';

import { AUTH_MODE, unwrap } from '../api/client';
import { forgotPassword, register, resendVerification } from '../api/gen';
import { oidcConfigured } from '../lib/oidc';
import { useSession } from '../lib/session';

/**
 * The only page a caller with no credential sees.
 *
 * Under authit — the default — the credential is an address and a password
 * this server keeps, so signing up, confirming and resetting all start here.
 * Under Zitadel there is nothing to type: the hosted login owns the flow, and
 * this page's whole job is to start it. Under the header seam it takes a
 * subject, which is what the Go suite and the end-to-end run send — exactly
 * as unsafe as it sounds, and why it is a build-time setting the product
 * bundle cannot be talked into.
 */
export function SignInPage() {
  return (
    <Center mih="100vh" p="md">
      <Card withBorder p="xl" w={400} maw="100%">
        <Stack>
          <Title order={3}>mindvm</Title>
          {AUTH_MODE === 'authit' ? (
            <PasswordPanels />
          ) : AUTH_MODE === 'zitadel' ? (
            <Zitadel />
          ) : (
            <HeaderSeam />
          )}
        </Stack>
      </Card>
    </Center>
  );
}

type Panel = 'signIn' | 'register' | 'forgot';

function PasswordPanels() {
  const { signInPassword } = useSession();
  const [panel, setPanel] = useState<Panel>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // One runner for all three panels: they differ in the call and in what they
  // say afterwards, not in how a failure is shown.
  const run = (what: () => Promise<string>) => {
    setBusy(true);
    setError('');
    setNotice('');
    void (async () => {
      try {
        setNotice(await what());
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusy(false);
      }
    })();
  };

  const go = (next: Panel) => {
    setPanel(next);
    setError('');
    setNotice('');
  };

  const messages = (
    <>
      {error && (
        <Alert color="red" variant="light" role="alert">
          {error}
        </Alert>
      )}
      {notice && (
        <Alert color="green" variant="light">
          {notice}
        </Alert>
      )}
    </>
  );

  const emailField = (
    <TextInput
      label="Email"
      type="email"
      autoComplete="email"
      value={email}
      onChange={(e) => setEmail(e.currentTarget.value)}
    />
  );

  if (panel === 'register') {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!email || !password) return;
          run(async () => {
            await unwrap(register({ body: { email, password } }));
            setPassword('');
            return 'We sent you an email. Confirm the address with the link in it, then sign in.';
          });
        }}
      >
        <Stack>
          <Text c="dimmed" size="sm">
            Create an account. Once you have confirmed your address you can found an
            organisation or be added to one.
          </Text>
          {messages}
          {emailField}
          <PasswordInput
            label="Password"
            description="At least twelve characters."
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
          />
          <Button type="submit" disabled={!email || !password} loading={busy}>
            Create account
          </Button>
          <Group justify="space-between">
            <Anchor size="sm" component="button" type="button" onClick={() => go('signIn')}>
              I already have an account
            </Anchor>
            <Anchor
              size="sm"
              component="button"
              type="button"
              disabled={!email}
              onClick={() =>
                run(async () => {
                  await unwrap(resendVerification({ body: { email } }));
                  return 'If this address has an unconfirmed account, the email is on its way.';
                })
              }
            >
              Send the email again
            </Anchor>
          </Group>
        </Stack>
      </form>
    );
  }

  if (panel === 'forgot') {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!email) return;
          run(async () => {
            await unwrap(forgotPassword({ body: { email } }));
            return 'If this address has an account, a link to choose a new password is in its inbox.';
          });
        }}
      >
        <Stack>
          {messages}
          {emailField}
          <Button type="submit" disabled={!email} loading={busy}>
            Send the link
          </Button>
          <Anchor size="sm" component="button" type="button" onClick={() => go('signIn')}>
            Back to sign-in
          </Anchor>
        </Stack>
      </form>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!email || !password) return;
        run(async () => {
          await signInPassword(email, password);
          return '';
        });
      }}
    >
      <Stack>
        {messages}
        {emailField}
        <PasswordInput
          label="Password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.currentTarget.value)}
        />
        <Button type="submit" disabled={!email || !password} loading={busy}>
          Sign in
        </Button>
        <Group justify="space-between">
          <Anchor size="sm" component="button" type="button" onClick={() => go('register')}>
            Create an account
          </Anchor>
          <Anchor size="sm" component="button" type="button" onClick={() => go('forgot')}>
            Forgot your password?
          </Anchor>
        </Group>
      </Stack>
    </form>
  );
}

function Zitadel() {
  const { signIn } = useSession();
  const [error, setError] = useState<string | null>(null);
  if (!oidcConfigured()) {
    return (
      <Alert color="yellow" variant="light">
        This build was given no Zitadel (VITE_ZITADEL_ISSUER, VITE_ZITADEL_CLIENT_ID).
      </Alert>
    );
  }
  return (
    <>
      <Text c="dimmed" size="sm">
        Sign in with your organisation account.
      </Text>
      {error && (
        <Alert color="red" variant="light" role="alert">
          {error}
        </Alert>
      )}
      <Button
        onClick={() =>
          signIn().catch((err) => setError(err instanceof Error ? err.message : String(err)))
        }
      >
        Sign in
      </Button>
    </>
  );
}

function HeaderSeam() {
  const { setCredential } = useSession();
  const [subject, setSubject] = useState('');
  return (
    <>
      <Text c="dimmed" size="sm">
        This build trusts the subject you name. It is the development seam, not a login.
      </Text>
      <TextInput
        label="Subject"
        placeholder="user-acme-owner"
        value={subject}
        onChange={(e) => setSubject(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && subject) setCredential(subject);
        }}
      />
      <Button disabled={!subject} onClick={() => setCredential(subject)}>
        Continue
      </Button>
    </>
  );
}
